import { shallowRef } from 'vue';
import type { DropdownItem } from '../Dropdown';
import type { ContextMenuInstance } from './ContextMenu.model';

const useContextMenuService = () => {
	// shallow: the instance carries a `beforeComponent`, and a deep ref would
	// turn that component definition into a reactive proxy. Only ever replaced
	// wholesale, never mutated in place.
	const menu = shallowRef<ContextMenuInstance | null>(null);

	const open = (nextMenu: ContextMenuInstance) => {
		if (menu.value && menu.value.id !== nextMenu.id) {
			menu.value.onClose?.();
		}

		menu.value = nextMenu;
		return nextMenu.id;
	};

	const close = (id?: string) => {
		if (!menu.value) {
			return;
		}

		if (id && menu.value.id !== id) {
			return;
		}

		const closingMenu = menu.value;
		menu.value = null;
		closingMenu.onClose?.();
	};

	const select = (item: DropdownItem) => {
		const activeMenu = menu.value;
		if (!activeMenu || item.disabled || item.locked) {
			return;
		}

		item.action?.(item);
		activeMenu.onSelect?.(item);

		if (activeMenu.closeOnSelect !== false) {
			close(activeMenu.id);
		}
	};

	return {
		menu,
		open,
		show: open,
		close,
		hide: close,
		clear: () => close(),
		select,
	};
};

export const contextMenuService = useContextMenuService();
export type ContextMenuService = ReturnType<typeof useContextMenuService>;
